import { get } from 'lodash';

import Constants from '../../shared/constants';
import { cspShippingRegionSupported } from '../user_helper';
import UserStore from '../../../reverb-js-common/user_store';
import * as elog from '@reverbdotcom/commons/src/elog';
import type { Price } from './api_comparison_shopping_page';

const GRID_IMAGE_KEY = '_links.grid_image.href';
const HORIZONTAL_IMAGE_KEY = '_links.mobile_hero_image.href';

interface Brand {
  name: string;
}

export default class ComparisonShoppingPage {
  id: string;

  brand: Brand;

  slug: string;

  _links: any;

  title: string;

  newLowPrice?: Price;

  newListingsCount: number;

  usedLowPrice?: Price;

  usedListingsCount: number;

  heroImage: any;

  rootCategorySlug: string;

  constructor(cspJSON: any) {
    this.id = cspJSON.id;
    this.brand = cspJSON.brand;
    this._links = cspJSON._links;
    this.title = cspJSON.title;
    this.usedLowPrice = cspJSON.used_low_price || {};
    this.newLowPrice = cspJSON.new_low_price || {};
    this.usedListingsCount = cspJSON.used_total;
    this.newListingsCount = cspJSON.new_total;
    this.heroImage = cspJSON.hero_image;
    this.slug = cspJSON.slug;
    this.rootCategorySlug = cspJSON.root_category_slug;
  }

  get category() {
    return this.rootCategorySlug || '';
  }

  gridImageHref() {
    return get(this, GRID_IMAGE_KEY);
  }

  horizontalImageHref() {
    return get(this, HORIZONTAL_IMAGE_KEY);
  }

  hasGridImage() {
    return !!this.gridImageHref();
  }

  hasHorizontalImage() {
    return !!this.horizontalImageHref();
  }

  webLink() {
    if (cspShippingRegionSupported(UserStore.getState().shippingRegionCode)) {
      return this._links.web.href;
    }

    return this.globalLink(this._links.web.href);
  }

  globalLink(url: string) {
    const anchors = url.match(/#.+$/); // matches '#new' in 'url.com/something#new'
    if (anchors) {
      const urlWithoutAnchor = url.replace(anchors[0], '');
      return `${urlWithoutAnchor}?ships_to=${Constants.EVERYWHERE_CODE}${
        anchors[0]
      }`;
    }

    return `${url}?ships_to=${Constants.EVERYWHERE_CODE}`;
  }

  lowestPrice() {
    const prices = [];

    if (Object.keys(this.usedLowPrice).length > 0) {
      prices.push(this.usedLowPrice);
    }

    if (Object.keys(this.newLowPrice).length > 0) {
      prices.push(this.newLowPrice);
    }

    if (prices.length === 1) {
      return prices[0];
    }

    const lowestPrice = prices
      .filter(price => price)
      .sort((p1, p2) => p1.amount_cents - p2.amount_cents)[0];

    return lowestPrice;
  }

  lowestPriceIsUsed() {
    if (!this.usedLowPrice.amount_cents) {
      return false;
    }
    return this.lowestPrice().amount_cents === this.usedLowPrice.amount_cents;
  }

  lowestPriceIsNew() {
    if (!this.newLowPrice.amount_cents) {
      return false;
    }
    return this.lowestPrice().amount_cents === this.newLowPrice.amount_cents;
  }

  lowestPriceForEvent() {
    if (this.lowestPriceIsUsed()) {
      return {
        lowest_price_for_used: new elog.events.Price({
          amount: get(this.usedLowPrice, 'amount_cents'),
          currency: get(this.usedLowPrice, 'currency'),
        }),
      };
    }

    if (this.lowestPriceIsNew()) {
      return {
        lowest_price_for_new: new elog.events.Price({
          amount: get(this.newLowPrice, 'amount_cents'),
          currency: get(this.newLowPrice, 'currency'),
        }),
      };
    }

    return {};
  }

  asEventWithLowestPrice() {
    return new elog.events.ComparisonShoppingPage(
      ({ id: String(this.id), ...this.lowestPriceForEvent() }),
    );
  }

  asEvent() {
    return new elog.events.ComparisonShoppingPage({
      id: String(this.id),
      lowest_price_for_new: new elog.events.Price({
        amount: get(this, 'newLowPrice.amount_cents'),
        currency: get(this, 'newLowPrice.currency'),
      }),
      lowest_price_for_used: new elog.events.Price({
        amount: get(this, 'usedLowPrice.amount_cents'),
        currency: get(this, 'usedLowPrice.currency'),
      }),
      used_listing_count: this.usedListingsCount,
      new_listing_count: this.newListingsCount,
    });
  }
}
